// community-view.ts — the COMMUNITY screen, VIEW half (HTML/DOM UI shell). One full-viewport screen
// with three tabs (PLAYERS / GAMES / MY PROFILE) replacing the separate LEADERBOARD and MY PROFILE
// screens. Unlike the 240×160-stage screens, the content band uses the FULL browser viewport (the
// chat-overlay idiom: fixed inset-0 root, safe-area padded flex column) while keeping the WI chrome —
// title face, 9-slice buttons, the zip-in cascade, the steel-blue selection bar — and formScale() as
// the type-size reference so text tracks the shell's magnification. Tables reflow responsively:
// desktop shows the full column set, phone portrait drops to the core columns (the leaderboard-view
// PHONE_COL_KEYS approach). Browser-only; the fetch + state live in community-controller.ts.

import type {
  CommunityController,
  CommunityGameRow,
  CommunityTab,
} from './community-controller.ts';
import type { LeaderRow, LeaderSort } from './leaderboard-controller.ts';
import { LEADER_SORTS } from './leaderboard-controller.ts';
import { type ButtonArt } from './title-art.ts';
import { ksfxGuiButtonTap, ksfxGuiScrollingListSelectItem } from '../ui/uikit.ts';
import { zipInStage } from './formchrome.ts';
import { kszRegisterUrl, kszForgotAccountUrl } from '../ui/loginform.ts';
import {
  COL_BG,
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  el,
  formScale,
  formTitle,
  FONT_BODY,
  FONT_BUTTON,
  buttonWidthU,
  makeShellButton,
  ensureA11yStyles,
  isPortraitBoost,
  stageTextInput,
  mountViewportFit,
  type ViewportFit,
  withFocusPreserved,
} from './chrome.ts';
import { profileModal, fmtHours, fmtDate } from './profile-detail.ts';

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;
/** the content band's width cap — full-viewport, but a table wider than this reads worse, not better. */
const MAX_PANE_PX = 1100;

export interface CommunityViewDeps {
  controller: CommunityController;
  root: HTMLElement;
  /** hide the game canvas while the overlay is up (a pure menu screen — onExit re-renders the menu). */
  hideCanvas: () => void;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url. */
  titleBg?: string;
  /** the raw 9-slice button pieces — the SAME faithful chrome as the menu / lobby. */
  buttonArt?: ButtonArt;
  /** play a GUI sound effect (ksfxGui*) on press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** open an external URL in a new tab — the login REGISTER / FORGOT ACCOUNT pages. */
  openUrl?: (url: string) => void;
  /** BACK / Escape → the DOM main menu (the host swaps views). */
  onExit: () => void;
}

/** A table column: fixed design-unit width (w) or a flex share (flex); phone marks the
 *  phone-portrait subset (the leaderboard PHONE_COL_KEYS approach). */
interface Col {
  key: string;
  label: string;
  w?: number;
  flex?: number;
  align: 'left' | 'center' | 'right';
  phone?: boolean;
}

const PLAYER_COLS: Col[] = [
  // Wide enough for a 4-digit rank: the visible list is 1–25, but the "YOUR RANK" pin shows the
  // player's absolute rank among all ranked players (thousands), which clipped to "9…" at w:12.
  { key: 'rank', label: '#', w: 24, align: 'right', phone: true },
  { key: 'avatar', label: '', w: 12, align: 'center' },
  { key: 'name', label: 'Player', flex: 1, align: 'left', phone: true },
  { key: 'rating', label: 'Rating', w: 26, align: 'right', phone: true },
  { key: 'wl', label: 'W-L', w: 36, align: 'right', phone: true },
  { key: 'winp', label: 'Win%', w: 22, align: 'right', phone: true },
  { key: 'kd', label: 'K/D', w: 18, align: 'right' },
  { key: 'games', label: 'Games', w: 26, align: 'right' },
  { key: 'playtime', label: 'Time', w: 22, align: 'right' },
  { key: 'seen', label: 'Last seen', w: 42, align: 'right' },
];

const GAME_COLS: Col[] = [
  { key: 'when', label: 'When', w: 42, align: 'left', phone: true },
  { key: 'map', label: 'Map', flex: 1, align: 'left', phone: true },
  { key: 'players', label: 'Players', flex: 1.4, align: 'left', phone: true },
  { key: 'len', label: 'Len', w: 20, align: 'right' },
  { key: 'watch', label: '', w: 26, align: 'center', phone: true },
];

export class CommunityView {
  private readonly d: CommunityViewDeps;
  private entered = false;
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;
  /** keeps the sign-in fields clear of the soft keyboard (chrome.mountViewportFit). */
  private readonly viewport: ViewportFit;
  private readonly btn: (
    label: string,
    onClick: () => void,
    enabled?: boolean,
    size?: { w: number; h: number },
  ) => HTMLButtonElement;

  constructor(d: CommunityViewDeps) {
    this.d = d;
    this.btn = makeShellButton(d, 'sub');
    ensureA11yStyles();
    const s = d.root.style;
    s.position = 'fixed';
    // inset:0 is the RESTING box; chrome.mountViewportFit then narrows the root to the VISUAL
    // so the sign-in fields stay in the band ABOVE an open soft keyboard (chrome.ts).
    s.inset = '0';
    s.display = 'flex';
    s.flexDirection = 'column';
    s.alignItems = 'stretch';
    s.background = COL_BG;
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.zIndex = '10';
    s.overflow = 'hidden';
    // A height-only resize is the soft keyboard, and re-rendering there would
    // replaceChildren() away the focused sign-in field, dismissing the keyboard. Only a WIDTH
    // change is a real orientation/window change worth rebuilding for. See chrome.ts. (No
    // stage here — this screen is a 100%×100% flex frame that reflows with the root.)
    this.viewport = mountViewportFit(d.root, {
      rebuild: () => this.render(),
      stage: () => null,
      // the host hides this root while the in-client replay viewer is up but keeps the view
      // alive; without this a resize would re-show the screen and re-hide the game canvas
      // (the same guard the lobby has for an in-progress match).
      skip: () => d.root.style.display === 'none',
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  // Escape → close the open player-detail modal first, else BACK out of the screen.
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    // an overlay (the in-client replay viewer) is up over us — its own Escape handling owns the key.
    if (this.d.root.style.display === 'none') return;
    e.preventDefault();
    if (this.d.controller.players.detailName !== null) {
      this.d.controller.players.closeDetail();
      return;
    }
    this.d.onExit();
  };

  destroy(): void {
    this.viewport.detach();
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
  }

  render(): void {
    const root = this.d.root;
    this.d.hideCanvas();
    root.style.display = 'flex';
    root.style.backgroundImage = this.d.titleBg !== undefined ? `url(${this.d.titleBg})` : 'none';
    root.style.backgroundColor = COL_BG;
    root.style.backgroundSize = 'cover';
    root.style.backgroundPosition = 'center';
    root.style.backgroundRepeat = 'no-repeat';

    // preserve list scroll across the full re-render (data updates / MORE appends).
    const scrolls = new Map<string, number>();
    for (const n of root.querySelectorAll('[data-comm-scroll]')) {
      scrolls.set((n as HTMLElement).dataset.commScroll!, (n as HTMLElement).scrollTop);
    }

    const screen = this.communityScreen();
    const children: HTMLElement[] = [screen];
    if (this.d.controller.players.detailName !== null) children.push(this.detailModal());
    // replaceChildren destroys the focused sign-in <input>, which blurs it and drops the soft
    // keyboard. (Typing itself does NOT re-render — profile-controller's setLoginName /
    // setLoginPassword don't notify — but any controller push while typing does.)
    withFocusPreserved(root, () => {
      root.replaceChildren(...children);
      this.viewport.fit(); // re-fit before the scroll restore below forces layout

      for (const n of root.querySelectorAll('[data-comm-scroll]')) {
        const saved = scrolls.get((n as HTMLElement).dataset.commScroll!);
        if (saved !== undefined) (n as HTMLElement).scrollTop = saved;
      }
    });

    if (!this.entered) {
      this.entered = true;
      if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
      this.formShowTimer = zipInStage(screen, this.d.playSfx);
    }
  }

  // ── the screen frame: title, tab bar, content band, footer buttons ─────────────
  private communityScreen(): HTMLElement {
    const s = formScale();
    const frame = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
      // safe-area insets (the chat-overlay idiom) + a base margin.
      padding:
        `max(${4 * s}px, env(safe-area-inset-top)) max(${4 * s}px, env(safe-area-inset-right)) ` +
        `max(${3 * s}px, env(safe-area-inset-bottom)) max(${4 * s}px, env(safe-area-inset-left))`,
    });
    frame.dataset.community = '1';

    const title = formTitle('COMMUNITY', s);
    title.style.width = '100%';
    title.style.flex = '0 0 auto';
    frame.append(title);
    frame.append(this.tabBar(s));

    const content = el('div', {
      flex: '1 1 0',
      minHeight: '0',
      width: '100%',
      maxWidth: `${MAX_PANE_PX}px`,
      display: 'flex',
      flexDirection: 'column',
      marginTop: `${2 * s}px`,
    });
    const c = this.d.controller;
    if (c.tab === 'players') content.append(this.playersPane(s));
    else if (c.tab === 'games') content.append(this.gamesPane(s));
    else content.append(this.profilePane(s));
    frame.append(content);

    frame.append(this.footerRow(s));
    return frame;
  }

  // the main tab bar — the sort-tab idiom (underlined active tab), sized up for a top-level control.
  private tabBar(s: number): HTMLElement {
    const c = this.d.controller;
    const tabs = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      flex: '0 0 auto',
      marginTop: `${1 * s}px`,
    });
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-label', 'Community sections');
    const items: Array<{ id: CommunityTab; label: string }> = [
      { id: 'players', label: 'LEADERBOARD' },
      { id: 'games', label: 'GAMES' },
      { id: 'profile', label: 'MY PROFILE' },
    ];
    const portrait = isPortraitBoost();
    items.forEach((it, i) => {
      if (i > 0) {
        const sep = el('span', {
          color: 'rgba(232,238,240,0.4)',
          padding: `0 ${(portrait ? 2 : 4) * s}px`,
        });
        sep.textContent = '|';
        sep.setAttribute('aria-hidden', 'true');
        tabs.append(sep);
      }
      const active = it.id === c.tab;
      const t = el('button', {
        font: 'inherit',
        fontFamily: FONT_BUTTON,
        fontSize: `${Math.round((portrait ? 5 : 6) * s)}px`,
        letterSpacing: '0.04em',
        padding: `${1 * s}px ${1.5 * s}px`,
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        color: active ? COL_ACCENT : COL_FG,
        textDecoration: active ? 'underline' : 'none',
        textUnderlineOffset: `${1.5 * s}px`,
        textShadow: shadow,
      });
      t.textContent = it.label;
      t.setAttribute('role', 'tab');
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.dataset.commTab = it.id;
      t.addEventListener('click', () => {
        this.d.playSfx?.(ksfxGuiButtonTap);
        c.setTab(it.id);
      });
      tabs.append(t);
    });
    return tabs;
  }

  private footerRow(s: number): HTMLElement {
    const c = this.d.controller;
    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: `${6 * s}px`,
      flex: '0 0 auto',
      marginTop: `${3 * s}px`,
    });
    const h = 12;
    const mk = (label: string, on: () => void): HTMLElement =>
      this.btn(label, on, true, { w: buttonWidthU(label, h, s, 36) * s, h: h * s });
    row.append(mk('REFRESH', () => c.refresh()));
    row.append(mk('BACK', () => this.d.onExit()));
    return row;
  }

  // ── shared table bits ───────────────────────────────────────────────────────────
  private activeCols(cols: Col[]): Col[] {
    return isPortraitBoost() ? cols.filter((col) => col.phone === true) : cols;
  }

  private cellBox(col: Col, s: number): HTMLElement {
    const e = el('div', {
      textAlign: col.align,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    });
    if (col.w !== undefined) {
      e.style.width = `${col.w * s}px`;
      e.style.flex = '0 0 auto';
    } else {
      e.style.flex = `${col.flex ?? 1} 1 0`;
      e.style.minWidth = '0';
    }
    return e;
  }

  private headerRow(cols: Col[], s: number): HTMLElement {
    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: `${2 * s}px`,
      padding: `0 ${4 * s}px`,
      flex: '0 0 auto',
    });
    for (const col of cols) {
      const cell = this.cellBox(col, s);
      Object.assign(cell.style, {
        fontFamily: FONT,
        fontSize: `${Math.round(5 * s)}px`,
        color: COL_FG,
        textShadow: shadow,
        opacity: '0.8',
        fontWeight: '700',
      });
      cell.textContent = col.label;
      row.append(cell);
    }
    return row;
  }

  /** the bordered scrolling list box every tab uses (the stageListBox look, flex-sized). */
  private listBox(scrollId: string, s: number): HTMLElement {
    const box = el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '0',
      overflowY: 'auto',
      boxSizing: 'border-box',
      width: '100%',
      flex: '1 1 0',
      minHeight: '0',
      background: 'rgba(7,22,27,0.55)',
      border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
    });
    box.dataset.list = '1'; // zip the list as a single unit (rows don't cascade)
    box.dataset.commScroll = scrollId;
    return box;
  }

  private noteText(text: string, s: number): HTMLElement {
    const note = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      opacity: '0.7',
      fontStyle: 'italic',
      textAlign: 'center',
      padding: `${4 * s}px`,
      margin: 'auto',
    });
    note.textContent = text;
    return note;
  }

  // ── PLAYERS tab (the leaderboard, + the dashboard's Playtime / Last seen columns) ──
  private playersPane(s: number): HTMLElement {
    const c = this.d.controller;
    const lb = c.players;
    const pane = el('div', {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: '0',
    });
    pane.append(this.sortTabs(s));
    const cols = this.activeCols(PLAYER_COLS);
    pane.append(this.headerRow(cols, s));

    const box = this.listBox('players', s);
    box.setAttribute('role', 'listbox');
    box.setAttribute('aria-label', 'Players');
    if (lb.status !== 'ready' || lb.rows.length === 0) {
      box.append(
        this.noteText(
          lb.status === 'loading'
            ? 'Loading players…'
            : lb.status === 'error'
              ? lb.error || 'The leaderboard is unavailable.'
              : 'No ranked players yet — play a multiplayer game!',
          s,
        ),
      );
    } else {
      lb.rows.forEach((r, i) => {
        box.append(this.playerRow(r, i === lb.selected || lb.isMe(r.name), cols, s));
      });
    }
    pane.append(box);

    // "your rank" pinned row when the player is outside the visible top N.
    if (lb.me !== null) {
      const me = lb.me;
      const pin = el('div', { flex: '0 0 auto', marginTop: `${1 * s}px` });
      const cap = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5 * s)}px`,
        fontWeight: '700',
        opacity: '0.8',
        textShadow: shadow,
        padding: `0 ${4 * s}px`,
      });
      cap.textContent = 'YOUR RANK';
      pin.append(cap, this.playerRow(me, true, cols, s));
      pane.append(pin);
    }
    return pane;
  }

  private sortTabs(s: number): HTMLElement {
    const lb = this.d.controller.players;
    const portrait = isPortraitBoost();
    const tabs = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      flex: '0 0 auto',
      marginBottom: `${1.5 * s}px`,
    });
    tabs.setAttribute('role', 'radiogroup');
    tabs.setAttribute('aria-label', 'Leaderboard sort');
    LEADER_SORTS.forEach((opt, i) => {
      if (i > 0) {
        const sep = el('span', {
          color: 'rgba(232,238,240,0.4)',
          padding: `0 ${(portrait ? 1.1 : 3) * s}px`,
        });
        sep.textContent = '|';
        sep.setAttribute('aria-hidden', 'true');
        tabs.append(sep);
      }
      const active = opt.id === lb.sort;
      const t = el('button', {
        font: 'inherit',
        fontFamily: FONT_BUTTON,
        fontSize: `${Math.round((portrait ? 4.6 : 5) * s)}px`,
        letterSpacing: '0.03em',
        padding: `${1 * s}px ${(portrait ? 0.7 : 1.5) * s}px`,
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        color: active ? COL_ACCENT : COL_FG,
        textDecoration: active ? 'underline' : 'none',
        textUnderlineOffset: `${1.5 * s}px`,
        textShadow: shadow,
      });
      t.textContent = opt.label;
      t.setAttribute('role', 'radio');
      t.setAttribute('aria-checked', active ? 'true' : 'false');
      t.addEventListener('click', () => {
        this.d.playSfx?.(ksfxGuiButtonTap);
        lb.setSort(opt.id as LeaderSort);
      });
      tabs.append(t);
    });
    return tabs;
  }

  private playerRow(r: LeaderRow, selected: boolean, cols: Col[], s: number): HTMLElement {
    const c = this.d.controller;
    const item = el('button', {
      display: 'flex',
      alignItems: 'center',
      gap: `${2 * s}px`,
      textAlign: 'left',
      padding: `${0.8 * s}px ${4 * s}px`,
      background: selected ? 'rgb(122, 168, 198)' : 'transparent',
      color: selected ? '#0c1a22' : COL_FG,
      border: 'none',
      cursor: 'pointer',
      font: 'inherit',
      fontFamily: FONT,
      fontSize: `${Math.round(4.8 * s)}px`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      flex: '0 0 auto',
      width: '100%',
      boxSizing: 'border-box',
      textShadow: selected ? 'none' : shadow,
    });
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', selected ? 'true' : 'false');
    item.addEventListener('mouseenter', () => {
      if (!selected) item.style.background = 'rgba(143,208,224,0.14)';
    });
    item.addEventListener('mouseleave', () => {
      if (!selected) item.style.background = 'transparent';
    });
    item.addEventListener('click', () => {
      this.d.playSfx?.(ksfxGuiScrollingListSelectItem);
      c.openPlayer(r.name);
    });
    for (const col of cols) {
      if (col.key === 'avatar') {
        item.append(this.avatarCell(r, col.w!, s));
        continue;
      }
      const cell = this.cellBox(col, s);
      if (col.key === 'name' && r.isBot) {
        // A rated AI opponent (ApexBot): name + a small "BOT" badge so it reads clearly against humans.
        cell.append(document.createTextNode(r.name), botBadge(s));
      } else {
        cell.textContent = playerCellText(col.key, r);
      }
      item.append(cell);
    }
    return item;
  }

  private avatarCell(r: LeaderRow, w: number, s: number): HTMLElement {
    const px = Math.round((w - 1) * s);
    const wrap = el('div', {
      width: `${w * s}px`,
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });
    const box = el('div', {
      width: `${px}px`,
      height: `${px}px`,
      boxSizing: 'border-box',
      overflow: 'hidden',
      background: r.avatar !== null ? 'transparent' : 'rgba(143,208,224,0.14)',
    });
    if (r.avatar !== null) {
      const img = el('img', {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }) as HTMLImageElement;
      img.src = r.avatar;
      img.alt = '';
      img.addEventListener('error', () => {
        img.style.display = 'none';
        box.style.background = 'rgba(143,208,224,0.14)';
      });
      box.append(img);
    }
    wrap.append(box);
    return wrap;
  }

  // ── GAMES tab (the dashboard's Recent Games, adapted in-game) ────────────────────
  private gamesPane(s: number): HTMLElement {
    const c = this.d.controller;
    const pane = el('div', {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: '0',
    });
    if (c.summary !== null) pane.append(this.summaryStrip(s));
    pane.append(this.filterRow(s));
    const cols = this.activeCols(GAME_COLS);
    pane.append(this.headerRow(cols, s));

    const box = this.listBox('games', s);
    box.setAttribute('aria-label', 'Recent games');
    const rows = c.visibleGames();
    if (c.gamesStatus !== 'ready' || rows.length === 0) {
      box.append(
        this.noteText(
          c.gamesStatus === 'loading'
            ? 'Loading games…'
            : c.gamesStatus === 'error'
              ? c.gamesError || 'The games feed is unavailable.'
              : c.games.length > 0
                ? 'Only quiet games recently — SHOW ALL to see them.'
                : 'No multiplayer games yet.',
          s,
        ),
      );
    } else {
      for (const g of rows) box.append(this.gameRow(g, cols, s));
      if (c.hasMoreGames()) {
        const moreWrap = el('div', {
          display: 'flex',
          justifyContent: 'center',
          padding: `${2 * s}px`,
          flex: '0 0 auto',
        });
        const label = c.loadingMore ? 'LOADING…' : 'MORE';
        moreWrap.append(
          this.btn(label, () => void c.moreGames(), !c.loadingMore, {
            w: buttonWidthU(label, 10, s, 30) * s,
            h: 10 * s,
          }),
        );
        box.append(moreWrap);
      }
    }
    pane.append(box);
    return pane;
  }

  // the summary strip: games today / this week / the week's most-played map.
  private summaryStrip(s: number): HTMLElement {
    const sum = this.d.controller.summary!;
    const strip = el('div', {
      display: 'flex',
      justifyContent: 'center',
      gap: `${3 * s}px`,
      flexWrap: 'wrap',
      flex: '0 0 auto',
      marginBottom: `${2 * s}px`,
    });
    const chip = (label: string, value: string): HTMLElement => {
      const box = el('div', {
        display: 'flex',
        alignItems: 'baseline',
        gap: `${1.5 * s}px`,
        padding: `${1 * s}px ${3 * s}px`,
        background: 'rgba(7,22,27,0.55)',
        border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
      });
      const v = el('span', {
        fontFamily: FONT_BUTTON,
        fontSize: `${Math.round(6 * s)}px`,
        color: COL_ACCENT,
        textShadow: shadow,
      });
      v.textContent = value;
      const k = el('span', {
        fontFamily: FONT,
        fontSize: `${Math.round(4.5 * s)}px`,
        color: COL_FG,
        opacity: '0.75',
        textShadow: shadow,
      });
      k.textContent = label;
      box.append(v, k);
      return box;
    };
    strip.append(chip(String(sum.today), 'today'));
    strip.append(chip(String(sum.week), 'this week'));
    if (sum.topMap !== null) strip.append(chip(sum.topMap, 'top map'));
    return strip;
  }

  // the quiet-recording filter toggle (replay-browse semantics: <50KB = a test/no-action game).
  private filterRow(s: number): HTMLElement {
    const c = this.d.controller;
    const row = el('div', {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flex: '0 0 auto',
      marginBottom: `${0.5 * s}px`,
    });
    const hidden = c.hiddenGameCount();
    if (!c.showAllGames && hidden === 0 && c.gamesStatus === 'ready') return row;
    if (c.gamesStatus !== 'ready') return row;
    const t = el('button', {
      font: 'inherit',
      fontFamily: FONT,
      fontSize: `${Math.round(4.5 * s)}px`,
      padding: `${0.5 * s}px ${1 * s}px`,
      cursor: 'pointer',
      border: 'none',
      background: 'transparent',
      color: COL_ACCENT,
      textDecoration: 'underline',
      textUnderlineOffset: `${1.5 * s}px`,
      textShadow: shadow,
    });
    t.textContent = c.showAllGames ? 'HIDE QUIET GAMES' : `SHOW ALL (+${hidden} quiet)`;
    t.dataset.commShowAll = '1';
    t.addEventListener('click', () => {
      this.d.playSfx?.(ksfxGuiButtonTap);
      c.toggleShowAllGames();
    });
    row.append(t);
    return row;
  }

  private gameRow(g: CommunityGameRow, cols: Col[], s: number): HTMLElement {
    const c = this.d.controller;
    const row = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: `${2 * s}px`,
      padding: `${0.8 * s}px ${4 * s}px`,
      fontFamily: FONT,
      fontSize: `${Math.round(4.8 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      flex: '0 0 auto',
      boxSizing: 'border-box',
      width: '100%',
    });
    for (const col of cols) {
      const cell = this.cellBox(col, s);
      switch (col.key) {
        case 'when':
          cell.textContent = fmtWhen(g.endUtc);
          cell.style.opacity = '0.8';
          break;
        case 'map':
          cell.textContent = g.map;
          break;
        case 'players':
          this.fillPlayersCell(cell, g, s);
          break;
        case 'len':
          cell.textContent = fmtLen(g.durationSecs);
          cell.style.opacity = '0.8';
          break;
        case 'watch':
          if (g.hasReplay) {
            // flush the ▶ RIGHT in its column: the leftover column width becomes breathing room
            // between the Len number and the button (instead of centering slack on both sides).
            Object.assign(cell.style, { display: 'flex', justifyContent: 'flex-end' });
            const b = this.btn('▶', () => c.watchReplay(g.replayKey), true, {
              w: buttonWidthU('▶', 9, s, 14) * s,
              h: 9 * s,
            });
            b.setAttribute('aria-label', 'Watch replay');
            b.title = 'Watch replay';
            cell.append(b);
          }
          break;
      }
      row.append(cell);
    }
    return row;
  }

  // the roster cell: winners in accent, anon names dimmed, registered names click → profile modal;
  // dropped bot seats shown as a muted count.
  private fillPlayersCell(cell: HTMLElement, g: CommunityGameRow, s: number): void {
    const c = this.d.controller;
    g.players.forEach((p, i) => {
      if (i > 0) {
        const sep = el('span', { opacity: '0.5' });
        sep.textContent = ', ';
        cell.append(sep);
      }
      const span = el(p.registered ? 'button' : 'span', {
        color: p.winner ? COL_ACCENT : 'inherit',
        fontWeight: p.winner ? '700' : '400',
        opacity: p.anon && !p.winner ? '0.6' : '1',
      });
      span.textContent = p.name;
      if (p.registered) {
        Object.assign(span.style, {
          font: 'inherit',
          fontFamily: FONT,
          fontSize: 'inherit',
          border: 'none',
          background: 'transparent',
          padding: '0',
          cursor: 'pointer',
          textDecoration: 'underline',
          textUnderlineOffset: `${1 * s}px`,
          textShadow: shadow,
        });
        span.addEventListener('click', () => {
          this.d.playSfx?.(ksfxGuiScrollingListSelectItem);
          c.openPlayer(p.name);
        });
      }
      cell.append(span);
    });
    if (g.bots > 0) {
      const b = el('span', { opacity: '0.5' });
      b.textContent = `${g.players.length > 0 ? ' ' : ''}+${g.bots} bot${g.bots > 1 ? 's' : ''}`;
      cell.append(b);
    }
  }

  // ── MY PROFILE tab ────────────────────────────────────────────────────────────
  // The profile laid out INLINE in the same translucent bordered pane the other tabs use — the
  // boxed profileModal stays for the player-detail popups (detailModal below), but a tab that
  // renders a modal inside itself reads as a stray dialog, not a pane.
  private profilePane(s: number): HTMLElement {
    const c = this.d.controller;
    const p = c.profile;
    const pane = el('div', {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: '0',
    });
    if (p.mode === 'login') {
      // This screen has no design stage to rescale, so when the soft keyboard collapses the
      // band the sign-in form is simply taller than the space left — and the root clips
      // (overflow:hidden) with the page itself unscrollable. Let the pane scroll so the
      // Password row and the buttons stay reachable instead of being cut off.
      pane.style.overflowY = 'auto';
      pane.append(this.loginForm(s));
      return pane;
    }

    const box = this.listBox('profile', s);
    box.setAttribute('aria-label', 'My profile');
    box.style.padding = `${3 * s}px ${4 * s}px`;

    // header: the signed-in name + a right-anchored SIGN OUT (present in every non-login state,
    // like the modal's header bar, so a stuck 'empty'/'error' profile can still sign out).
    const header = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: `${4 * s}px`,
      flex: '0 0 auto',
      marginBottom: `${2 * s}px`,
    });
    const nameEl = el('div', {
      fontFamily: FONT_BUTTON,
      fontSize: `${Math.round(7 * s)}px`,
      color: COL_ACCENT,
      textShadow: shadow,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    nameEl.textContent = p.mode === 'ready' && p.profile !== null ? p.profile.name : p.name;
    const signOut = this.btn('SIGN OUT', () => p.signOut(), true, {
      w: buttonWidthU('SIGN OUT', 10, s, 30) * s,
      h: 10 * s,
    });
    header.append(nameEl, signOut);
    box.append(header);

    if (p.mode !== 'ready' || p.profile === null) {
      box.append(
        this.noteText(
          p.mode === 'loading'
            ? 'Loading profile…'
            : p.mode === 'empty'
              ? 'Play a multiplayer game to start your profile.'
              : 'Could not load your profile.',
          s,
        ),
      );
      pane.append(box);
      return pane;
    }

    const prof = p.profile;
    const cb = prof.combat;
    const line = (label: string, value: string): HTMLElement => {
      const e = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5 * s)}px`,
        color: COL_FG,
        textShadow: shadow,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      });
      const k = el('span', { opacity: '0.7' });
      k.textContent = `${label}: `;
      const v = el('span', {});
      v.textContent = value;
      e.append(k, v);
      return e;
    };
    // the same lifetime lines the profile modal shows, reflowed into a responsive grid that
    // uses the pane width (two columns on desktop, one on a narrow viewport).
    const grid = el('div', {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${Math.round(90 * s)}px), 1fr))`,
      gap: `${1.2 * s}px ${8 * s}px`,
      flex: '0 0 auto',
      marginBottom: `${3 * s}px`,
    });
    const lines: Array<[string, string]> = [
      ['Rating', String(Math.round(prof.rating))],
      ['Record', `${prof.wins}-${prof.losses}  (${Math.round(prof.winRate * 100)}% win)`],
      ['Units (killed / lost)', `${cb.unitsKilled} / ${cb.unitsLost}  (K/D ${cb.kd.toFixed(1)})`],
      ['Structures (killed / lost)', `${cb.structsKilled} / ${cb.structsLost}`],
      ['Credits acquired', cb.creditsAcquired.toLocaleString()],
      ['Playtime', fmtHours(cb.playtimeSecs)],
      ['Member since', fmtDate(prof.createdUtc)],
      ['Last seen', fmtDate(prof.lastGameUtc)],
    ];
    for (const [k, v] of lines) grid.append(line(k, v));
    box.append(grid);

    const recentCap = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5 * s)}px`,
      fontWeight: '700',
      color: COL_ACCENT,
      textShadow: shadow,
      flex: '0 0 auto',
      marginBottom: `${1 * s}px`,
    });
    recentCap.textContent = 'RECENT GAMES';
    box.append(recentCap);

    const recent = prof.recentGames ?? [];
    if (recent.length === 0) {
      const none = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5 * s)}px`,
        color: COL_FG,
        opacity: '0.6',
        textShadow: shadow,
      });
      none.textContent = 'No recent games.';
      box.append(none);
    } else {
      for (const g of recent) {
        const row = el('div', {
          display: 'flex',
          alignItems: 'center',
          gap: `${2 * s}px`,
          padding: `${0.6 * s}px 0`,
          fontFamily: FONT,
          fontSize: `${Math.round(5 * s)}px`,
          color: COL_FG,
          textShadow: shadow,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          flex: '0 0 auto',
        });
        const wl = el('span', {
          color: g.won ? '#7ec97e' : '#e08a8a',
          width: `${6 * s}px`,
          flex: '0 0 auto',
        });
        wl.textContent = g.won ? 'W' : 'L';
        const map = el('span', {
          flex: '1 1 0',
          minWidth: '0',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        });
        map.textContent = g.map;
        const date = el('span', { opacity: '0.7', flex: '0 0 auto' });
        date.textContent = fmtDate(g.endUtc);
        row.append(wl, map, date);
        if (g.hasReplay) {
          const play = this.btn('▶', () => c.watchReplay(g.replayKey), true, {
            w: buttonWidthU('▶', 9, s, 14) * s,
            h: 9 * s,
          });
          play.setAttribute('aria-label', 'Play replay');
          play.title = 'Play replay';
          row.append(play);
        }
        box.append(row);
      }
      const cap = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(4.5 * s)}px`,
        color: COL_FG,
        opacity: '0.55',
        fontStyle: 'italic',
        textShadow: shadow,
        flex: '0 0 auto',
        marginTop: `${1.5 * s}px`,
      });
      cap.textContent = 'Unranked games don’t affect your record.';
      box.append(cap);
    }
    pane.append(box);
    return pane;
  }

  // the named sign-in form (no anonymous option), inline in the tab pane — the profile-view
  // login screen reflowed from the 240×160 stage into a centered flex column.
  private loginForm(s: number): HTMLElement {
    const c = this.d.controller;
    const p = c.profile;
    const form = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: `${3 * s}px`,
      margin: 'auto',
      width: '100%',
      maxWidth: `${Math.min(150 * s, 480)}px`,
      padding: `${4 * s}px`,
      boxSizing: 'border-box',
      background: 'rgba(7,22,27,0.55)',
      border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
    });
    const label = (text: string): HTMLElement => {
      const e = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5.5 * s)}px`,
        color: COL_FG,
        textShadow: shadow,
      });
      e.textContent = text;
      return e;
    };
    form.append(label('Sign in to view your profile:'));

    const field = (input: HTMLInputElement): HTMLElement => {
      const box = el('div', { height: `${11 * s}px`, width: '100%' });
      box.append(input);
      return box;
    };
    const nameIn = stageTextInput('profileName', 'Player name', p.loginName, s);
    nameIn.addEventListener('input', () => {
      p.setLoginName(nameIn.value);
    });
    form.append(label('Player Name:'), field(nameIn));

    const pwIn = stageTextInput('profilePassword', 'Password', p.loginPassword, s);
    pwIn.type = 'password';
    pwIn.addEventListener('input', () => {
      p.setLoginPassword(pwIn.value);
    });
    pwIn.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') void p.submitLogin();
    });
    form.append(label('Password:'), field(pwIn));

    if (p.loginStatus.length > 0) {
      const st = el('div', {
        fontFamily: FONT,
        fontSize: `${Math.round(5 * s)}px`,
        color: COL_FG,
        textShadow: shadow,
        textAlign: 'center',
      });
      st.textContent = p.loginStatus;
      form.append(st);
    }

    const btnRow = (btns: Array<{ label: string; on: () => void }>): HTMLElement => {
      const row = el('div', {
        display: 'flex',
        justifyContent: 'center',
        gap: `${4 * s}px`,
        flexWrap: 'wrap',
      });
      const h = 12;
      for (const b of btns)
        row.append(
          this.btn(b.label, b.on, true, { w: buttonWidthU(b.label, h, s, 36) * s, h: h * s }),
        );
      return row;
    };
    form.append(
      btnRow([
        { label: 'LOGIN', on: () => void p.submitLogin() },
        { label: 'REGISTER', on: () => this.d.openUrl?.(kszRegisterUrl) },
      ]),
    );
    form.append(
      btnRow([{ label: 'FORGOT ACCOUNT', on: () => this.d.openUrl?.(kszForgotAccountUrl) }]),
    );
    return form;
  }

  // ── the player-detail modal (shared profile modal over the whole screen) ─────────
  private detailModal(): HTMLElement {
    const c = this.d.controller;
    const lb = c.players;
    return profileModal({
      title: lb.detailName ?? 'PLAYER',
      status: lb.detailStatus,
      profile: lb.detail,
      btn: this.btn,
      onClose: () => lb.closeDetail(),
      onWatch: (key) => c.watchReplay(key),
    });
  }
}

// A small "BOT" pill appended after a rated AI opponent's name in the leaderboard/roster. Sized
// SMALLER than the player-name text (which is 4.8*s in playerRow) so it reads as a tag, not a heading.
function botBadge(s: number): HTMLElement {
  const b = el('span', {
    marginLeft: `${Math.round(2 * s)}px`,
    padding: `0 ${Math.round(1.4 * s)}px`,
    fontSize: `${Math.round(3 * s)}px`,
    fontWeight: 'bold',
    letterSpacing: '0.04em',
    lineHeight: `${Math.round(4.8 * s)}px`, // match the name line so the pill centers vertically
    color: '#0b1a20',
    background: 'rgba(143,208,224,0.9)',
    borderRadius: `${Math.round(1.6 * s)}px`,
    verticalAlign: 'middle',
    // Kill the row's inherited text-shadow: it exists to make LIGHT names legible on the busy map
    // backdrop, but here it's a dark shadow under dark text on a light pill — muddy and low-contrast.
    textShadow: 'none',
  });
  b.textContent = 'BOT';
  return b;
}

// row-cell text for a PLAYERS column key.
function playerCellText(key: string, r: LeaderRow): string {
  switch (key) {
    case 'rank':
      return String(r.rank);
    case 'name':
      return r.name;
    case 'rating':
      return String(Math.round(r.rating));
    case 'wl':
      return `${r.wins}-${Math.max(0, r.games - r.wins)}`;
    case 'winp':
      return `${Math.round(r.winRate * 100)}%`;
    case 'kd':
      return r.kd.toFixed(1);
    case 'games':
      return String(r.games);
    case 'playtime':
      return fmtHours(r.playtimeSecs);
    case 'seen':
      return fmtDate(r.lastGameUtc);
    default:
      return '';
  }
}

/** When a game ended: time-of-day for the last 24h, else the date. */
function fmtWhen(endUtc: number): string {
  if (endUtc <= 0) return '—';
  const d = new Date(endUtc * 1000);
  if (Date.now() - d.getTime() < 24 * 3600 * 1000) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return d.toISOString().slice(0, 10);
}

/** A game's length, compact: 45s / 12m / 1.5h. */
function fmtLen(secs: number): string {
  if (secs <= 0) return '—';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
}
